import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useResetPasswordMutation } from '../../store/api/authApi';
import { palette } from '../../theme/palette';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { language } = useAppSelector((state) => state.ui);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const token = (location.state as { token?: string })?.token || '';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });

  // Redirect if no token in state
  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.new_password) {
      setError(t('auth.resetPassword.passwordRequired'));
      return;
    }

    if (formData.new_password.length < 8) {
      setError(t('auth.resetPassword.passwordMinLength'));
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError(t('auth.resetPassword.passwordMismatch'));
      return;
    }

    try {
      await resetPassword({
        token,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      }).unwrap();
      setSuccess(t('auth.resetPassword.success'));
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: unknown) {
      const error = err as { data?: { message?: string; errors?: Record<string, string[]> } };
      if (error?.data?.errors) {
        const firstError = Object.values(error.data.errors).flat()[0];
        setError(firstError || error?.data?.message || t('auth.resetPassword.error'));
      } else {
        setError(error?.data?.message || t('auth.resetPassword.error'));
      }
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Card
        elevation={8}
        sx={{
          width: '100%',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('auth.resetPassword.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('auth.resetPassword.subtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="new_password"
              label={t('auth.resetPassword.newPassword')}
              type={showPassword ? 'text' : 'password'}
              value={formData.new_password}
              onChange={handleChange}
              required
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              sx={{
                mb: 2,
                '& .MuiInputBase-input': {
                  textAlign: language === 'ar' ? 'right' : 'left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="confirm_password"
              label={t('auth.resetPassword.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirm_password}
              onChange={handleChange}
              required
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              sx={{
                mb: 3,
                '& .MuiInputBase-input': {
                  textAlign: language === 'ar' ? 'right' : 'left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                '&:hover': {
                  background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
                },
              }}
            >
              {isLoading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.reset')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;

